import { InversionContainer } from '@/controller/container'
import { InjectionTokens } from '@/controller/tokens'

import { RouteBuilder } from '@/domain/builders/RouteBuilder'
import { UseCase } from '@/domain/core/UseCase'
import { Route } from '@/domain/models/Route/Route'
import { Waypoint } from '@/domain/models/Waypoint'
import { Result } from '@/domain/protocols/Result'
import { MapState } from '@/domain/states/MapState'
import { RouteState } from '@/domain/states/RouteState'
import { SettingsState } from '@/domain/states/SettingsState'

import { IHttpClient } from '@/infra/http/IHttpClient'
import type { ILogger } from '@/infra/logger/ILogger'
import { LoadingStateMutator } from '@/presentation/modules/Base/states/LoadingState'

class AddWaypointToRouteUseCase implements UseCase<Waypoint, Result<Route>> {
  private routeState: RouteState
  private mapState: MapState
  private settingsState: SettingsState
  private httpClient: IHttpClient
  private logger: ILogger

  constructor() {
    const injectionContainer = InversionContainer.getInstance().getContainer()
    this.routeState = injectionContainer.get<RouteState>(InjectionTokens.RouteState)
    this.mapState = injectionContainer.get<MapState>(InjectionTokens.MapState)
    this.settingsState = injectionContainer.get<SettingsState>(InjectionTokens.SettingsState)
    this.httpClient = injectionContainer.get<IHttpClient>(InjectionTokens.HttpClient)
    this.logger = injectionContainer.get<ILogger>(InjectionTokens.Logger)
  }

  execute(request: Waypoint): Result<Route> {
    LoadingStateMutator.setIsLoading(true)
    try {
      const routeStateSnapshot = this.routeState.getStateSnapshot()
      const mapStateSnapshot = this.mapState.getStateSnapshot()

      let route: Route = null
      let insertIndex: number = null

      if (routeStateSnapshot.activeRoute) {
        if (routeStateSnapshot.activeRoute.waypoints && routeStateSnapshot.activeRoute.waypoints.length > 1) {
          const routeBuilder = new RouteBuilder().copyFromRoute(routeStateSnapshot.activeRoute)

          if (this.settingsState.getSetting('waypointInsertionMode') === 'smart') {
            insertIndex = routeBuilder.addSmartWaypoint(request)
          } else if (this.settingsState.getSetting('waypointInsertionMode') === 'manual') {
            if (routeBuilder.waypoints.length < 2) {
              routeBuilder.addWaypoint(request)
            } else {
              routeBuilder.addWaypointOnPosition(request, routeBuilder.waypoints.length - 1)
            }
          } else {
            routeBuilder.addWaypoint(request)
          }

          route = routeBuilder.build()
        } else {
          route = new RouteBuilder().copyFromRoute(routeStateSnapshot.activeRoute).addWaypoint(request).build()
        }
      } else {
        route = new RouteBuilder().addWaypoint(request).build()
      }

      this.routeState.setActiveRoute(route)

      if (mapStateSnapshot.nextWaypointSequence && insertIndex! + 1 < mapStateSnapshot.nextWaypointSequence) {
        this.mapState.incrementWaypointSequence()
      }

      LoadingStateMutator.setIsLoading(false)
      return Result.ok(route)
    } catch (error) {
      this.logger.error('AddWaypointToRouteUseCase', String(error))
      LoadingStateMutator.setIsLoading(false)
      return Result.fail(error)
    }
  }
}

export { AddWaypointToRouteUseCase }
